import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import moment from 'moment';
import { RippleModule } from 'primeng/ripple';
import { TimeUnitValue } from '../contract/time-unit-value.contract';
import { TimeUnit } from '../contract/time-unit.contract';


@Component({
  selector: 'osp-timefence-day',
  standalone: true,
  imports: [RippleModule, NgClass],
  templateUrl: './timefence-day.component.html',
  styleUrl: './timefence-day.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TimefenceDayComponent),
    }
  ]
})
export class TimefenceDayComponent implements ControlValueAccessor {
  private interval = 30;
  protected itemsPerLine = 16;
  protected data = signal<Map<number, TimeUnit>>(this.generateData('00:00', this.interval, 'm'));
  protected disabled = signal(false);
  private onChange: (value: TimeUnitValue[]) => void = () => { };
  private onTouched: () => void = () => { };

  private selected = computed(() => {
    return new Map(
      [...this.data()].filter(([, { active }]) => active)
    );
  });

  writeValue(value: TimeUnitValue[]): void {
    const valueSet = new Set(value.map(this.getTimeUnitKey));
    const active = [...valueSet].filter(v => !(this.selected().has(v)));
    const inactive = [...this.selected().keys()].filter(v => !(valueSet.has(v)));
    const changes = [
      ...(active.map(timeUnitKey => ({ timeUnitKey, active: true }))),
      ...(inactive.map(timeUnitKey => ({ timeUnitKey, active: false }))),
    ];

    this.changeUnitActivity(changes);
  }

  registerOnChange(fn: (value: TimeUnitValue[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  handleClick(timeUnitKey: number, active: boolean): void {
    this.changeUnitActivity([{ timeUnitKey, active }]);
    this.emitChanges();
  }

  private emitChanges(): void {
    const changes = [...this.selected().values()].map(({ value }) => value);
    this.onChange(changes);
  }

  private changeUnitActivity(changes: { timeUnitKey: number, active: boolean }[]): void {
    this.data.update(value => {
      changes.forEach(({ timeUnitKey, active }) => {
        const unit = value.get(timeUnitKey);
        if (unit) {
          value.set(timeUnitKey, { ...unit, active });
        }
      });
      return new Map(value);
    });
  }

  private generateData(desiredStartTime: string, interval: number, period: moment.unitOfTime.Base): Map<number, TimeUnit> {
    const periodsInADay = moment.duration(1, 'day').as(period);

    const result = new Map<number, TimeUnit>();
    const startTimeMoment = moment(desiredStartTime, 'hh:mm');
    for (let i = 0; i < periodsInADay; i += interval) {
      startTimeMoment.add(i === 0 ? 0 : interval, period);
      const hour = startTimeMoment.hour();
      const minutes = startTimeMoment.minute();
      const value: TimeUnitValue = { hour, minutes };
      result.set(this.getTimeUnitKey(value), {
        value,
        label: this.getLabel(value),
        active: false,
      });
    }

    return result;
  };


  private getTimeUnitKey({ hour, minutes }: TimeUnitValue): number {
    return 100 * hour + minutes;
  }

  private getLabel({ hour, minutes }: TimeUnitValue): string {
    return moment(`${hour}:${minutes}`, 'hh:mm').format('HH:mm');
  }
}
